import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { getQueuesLOV, type QueueSummary } from 'api/queues/operations/getQueuesLOV';

import { EditReference } from './EditReference';

interface Props {
  // The stored `zone_name|queue_name` path, or null for "no queue" / use the default.
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (q: QueueSummary) => `${q.zone_name}|${q.queue_name}`;
const getLabel = (q: QueueSummary): string => `${q.queue_name} (${q.zone_name})`;

/**
 * Searchable dropdown for queues keyed on the `zone_name|queue_name` path — the shape the node config stores. Unlike the
 * id-keyed references, the stored value already carries both display parts, so the interim item built by splitting it has a
 * real label: the field reads correctly before the eager LOV even loads (the LOV only populates the option list). Selection
 * returns the composite path (or null when cleared).
 */
export const EditQueueReference = ({ value, onChange, label = 'Queue', mandatory, disabled }: Props) => {
  // Split the stored path into the partial summary EditReference selects on. Memoised on the path so the reference model
  // isn't rebuilt each render.
  const selected = useMemo<QueueSummary | null>(() => {
    if (value === null || value === '') return null;
    const [zone_name = '', queue_name = ''] = value.split('|');
    return { queue_id: 0, queue_name, zone_name };
  }, [value]);

  return (
    <EditReference<QueueSummary>
      label={label}
      value={selected}
      onChange={(q) => onChange(q !== null ? getKey(q) : null)}
      source={getQueuesLOV}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(q, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{q.queue_name}</Highlighter>
          </span>
          <span className="text-body-secondary">{q.zone_name}</span>
        </div>
      )}
    />
  );
};
